import { cn } from "@/shared/lib/utils";
import { LucideIcon, TrendingUp, TrendingDown } from "lucide-react";

interface KPICardProps {
  title: string;
  value: string;
  unit?: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  icon: LucideIcon;
  status?: "good" | "warning" | "critical";
  className?: string;
  trendLabel?: string;
}

export function KPICard({ title, value, unit, trend, icon: Icon, status = "good", className, trendLabel = "vs last shift" }: KPICardProps) {
  const statusColors = {
    good: "border-l-success",
    warning: "border-l-warning",
    critical: "border-l-destructive",
  };

  return (
    <div
      className={cn(
        "bg-card rounded-lg p-5 shadow-card border-l-4 transition-all hover:shadow-elevated",
        statusColors[status],
        className
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm text-muted-foreground font-medium">{title}</p>
          <div className="flex items-baseline gap-1 mt-2">
            <span className="text-3xl font-bold text-foreground">{value}</span>
            {unit && <span className="text-lg text-muted-foreground">{unit}</span>}
          </div>
          {trend && (
            <div
              className={cn(
                "flex items-center gap-1 mt-2 text-sm font-medium",
                trend.isPositive ? "text-success" : "text-destructive"
              )}
            >
              {trend.isPositive ? (
                <TrendingUp className="w-4 h-4" />
              ) : (
                <TrendingDown className="w-4 h-4" />
              )}
              <span>{Math.abs(trend.value)}% {trendLabel}</span>
            </div>
          )}
        </div>
        <div
          className={cn(
            "w-12 h-12 rounded-lg flex items-center justify-center",
            status === "good" && "bg-success/10 text-success",
            status === "warning" && "bg-warning/10 text-warning",
            status === "critical" && "bg-destructive/10 text-destructive"
          )}
        >
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
}
